import { Box } from "@chakra-ui/react";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAccount } from "wagmi";

import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  isNumber,
  RESTAKE_NOW,
  toFixedWithoutRounding,
} from "@constants/common";
import { MINT_LIMIT_CHAINS } from "@constants/chains";
import { EVENTS } from "@constants/reduxEvents";

import AddXSymbol from "@components/AddXSymbol";
import RecoveryNotice from "@components/RecoveryNotice";
import StakeConfirmation from "@components/StakeConfirmation";
import useDepositPoolPaused from "@hooks/useDepositPoolPaused";

import {
  APPROVE_STAKING_CLICK,
  CONNECT_WALLET_CLICK,
  MINT_LIMIT_CROSSED,
  STAKE_CTA,
} from "@constants/analytics";
import { ERDataType } from "@hooks/useExchangeRate";
import useLedgerLive from "@hooks/useLedgerLive";
import usePoolData from "@hooks/usePoolData";
import {
  emitReduxEvent,
  formatNumber,
  isAmountInput,
  isFloat,
  numberToCurrency,
  toStringPrecision,
  trackEvent,
} from "@lib/utils";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import useDappChain from "@hooks/useDappChain";
import { ETH, WETH, WETHE, WSTETH } from "@config/contractsConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import useDappConfig from "@hooks/useDappConfig";

import { updateUserData } from "@store";
import useDepositFee from "@hooks/useDepositFee";
import useDepositEstimate from "@hooks/useDepositEstimate";
import useUserType from "@hooks/useUserType";
import useAllowance from "@hooks/useAllowance";
import { useEstimatedGasFee } from "@hooks/useGasFeeEstimation";
import useMintLimit from "@hooks/useMintLimit";
import { mantle, tac } from "viem/chains";
import { useRouter } from "next/router";
import TokenInput from "../TokenInput";
import { Button } from "@kelp-dao/kernel-design-system";
import TransactionSummary from "./TransactionSummary";
import { EUnstakeMode, TUnstakeMode } from "./types";
import StakeWithHGEth from "../StakeWithHgEth";

const MAX_FRACTION_DIGITS = 4;

interface StakeComponentProps {
  conversionData: ERDataType;
  isActive: boolean;
}

const StakeComponent = ({ conversionData, isActive }: StakeComponentProps) => {
  // router params - stakeAmount, token
  const router = useRouter();
  const { amount: stakeAmountParam, token: tokenParam } = router.query;

  const dispatch = useDispatch();

  const [stakeAmount, setStakeAmount] = useState(
    isAmountInput(stakeAmountParam as string)
      ? (stakeAmountParam as string).trim()
      : "",
  );
  const [error, setError] = useState("");
  const [isStakeConfirmModal, setIsStakeConfirmMoal] = useState(false);
  const { isStaderLedgerUser } = useUserType();
  const [selectedMode, setSelectedMode] = useState<TUnstakeMode>(
    EUnstakeMode.STANDARD,
  );
  const {
    remainingLimit,
    remainingLimitRsEth,
    isLoading: mintLimitLoading,
  } = useMintLimit();

  const { config, isRestakeSupported, isYieldVaultSupportedChain } =
    useDappConfig();

  const { selectedERC20, referralId } = useSelector((state: any) => state.user);

  // Handle router hydration of the `amount` query param.
  useEffect(() => {
    if (!router.isReady || !stakeAmountParam) return;

    const candidate = (stakeAmountParam as string).trim();
    if (isAmountInput(candidate)) {
      // Valid decimal amount: seed the input from the URL.
      if (!stakeAmount) setStakeAmount(candidate);
    } else {
      // Invalid (e.g. 1e3, 0xFF, 1.2.3, Infinity): strip it from the URL so it
      // is never displayed or fed downstream to parseUnits.
      const query = { ...router.query };
      delete query.amount;
      router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      });
    }
  }, [router.isReady, stakeAmountParam, stakeAmount]);

  useEffect(() => {
    if (
      tokenParam &&
      Object.keys(config.tokens).includes(tokenParam as string)
    ) {
      dispatch(updateUserData({ selectedERC20: tokenParam as string }));
    }
  }, [tokenParam, dispatch, config.tokens]);

  const conversions = useMemo(() => {
    const conversionToERC20 = conversionData?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionData?.exchangeRate]);

  const { isEthereumChain } = useDappChain();
  const { chainId } = useAccount();

  const { isPaused: isDepositPoolPaused } = useDepositPoolPaused();

  const poolData = usePoolData({
    enabled:
      selectedERC20 !== WSTETH &&
      selectedERC20 !== WETH &&
      selectedERC20 !== WETHE &&
      ((isEthereumChain && selectedERC20 !== ETH) || !isEthereumChain),
    conversionRate: conversions.toERC20,
  });

  const isStakingFull = useMemo(() => {
    return [""].includes(selectedERC20);
  }, [selectedERC20]);

  const { erc20Allowance } = useAllowance();

  const { isConnected, address } = useAccount();

  const { nativeTokenBalance, tokensBalances, xTokenTokenBalance } =
    useTokenBalance();

  const { isLedgerConnected } = useLedgerLive();

  const tokenAmount =
    selectedERC20 === ETH
      ? nativeTokenBalance
      : tokensBalances[selectedERC20] || 0;

  const { openConnectModal } = useConnectModal();

  const { estimatedGasFee } = useEstimatedGasFee();

  const { formatted: depositFeeP } = useDepositFee();

  const { returns } = useDepositEstimate(stakeAmount);

  const isPoolSizeExceeded = useMemo(() => {
    if (selectedERC20 === ETH) return false;
    return poolData?.poolLimit && poolData?.usedValue
      ? poolData.usedValue >= poolData.poolLimit
      : false;
  }, [poolData?.poolLimit, poolData?.usedValue, selectedERC20]);

  const handleModeChange = useCallback((mode: TUnstakeMode) => {
    setSelectedMode(mode);
  }, []);

  const checkInput = useCallback(
    (value = stakeAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenAmount) {
        error = `You can't restake more than available balance in your wallet.`;
      } else if (
        poolData &&
        poolData.poolLimit &&
        (Number(value) > poolData.poolLimit ||
          (poolData.usedValue &&
            poolData.usedValue + Number(value) > poolData.poolLimit))
      ) {
        error = `You can't restake more than pool size.`;
      } else if (chainId && MINT_LIMIT_CHAINS.includes(chainId)) {
        if (mintLimitLoading) {
          error = `Fetching ${config.xtoken.symbol} mint limit`;
        } else if (!mintLimitLoading && !remainingLimit) {
          error = `Error fetching ${config.xtoken.symbol} mint limit, please try after sometime.`;
        } else if (remainingLimit && Number(value) > Number(remainingLimit)) {
          trackEvent(MINT_LIMIT_CROSSED, { amount_entered: value });
          error = `You can only mint ${remainingLimitRsEth} ${config.xtoken.symbol} until 23:59 UTC today. More ${config.xtoken.symbol} can be minted after UTC 00:00 tomorrow.`;
        }
      }

      return error;
    },
    [
      isConnected,
      nativeTokenBalance,
      poolData,
      selectedERC20,
      stakeAmount,
      tokensBalances,
      remainingLimit,
      remainingLimitRsEth,
    ],
  );

  useEffect(() => {
    if (stakeAmount !== "") {
      const error = checkInput(stakeAmount);
      setError(error);
    }
  }, [tokenAmount, nativeTokenBalance, address, stakeAmount, checkInput]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const priorityToken = Object.keys(config.tokens).find(
      (key) => config.tokens[key].priority,
    );

    if (!priorityToken) {
      const isNativeTokenRestakeSupported =
        config.nativeToken.isRestakeSupported;
      dispatch(
        updateUserData({
          selectedERC20:
            isNativeTokenRestakeSupported &&
            chainId !== tac.id &&
            chainId !== mantle.id
              ? config.nativeToken.symbol
              : Object.values(config.tokens)[0]?.symbol,
        }),
      );
      return;
    }

    dispatch(
      updateUserData({
        selectedERC20: tokenParam || priorityToken!,
      }),
    );
  }, [config.tokens, dispatch, isActive]);

  const isApproved = useMemo(() => {
    if (selectedERC20 === ETH) return true;

    const amount = stakeAmount || "";
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, selectedERC20, stakeAmount]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isAmountInput(val) || val === "") {
      if (val.startsWith("00") && !val.startsWith("00.")) {
        return;
      }

      if (val.indexOf(".") === -1) {
        setStakeAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setStakeAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else {
      if (!isRestakeSupported) {
        return "Switch network";
      } else if (isApproved) {
        return RESTAKE_NOW;
      }
      return APPROVE_CTA;
    }
  };

  const convertedAmount = useMemo(() => {
    if (stakeAmount && isNumber(stakeAmount)) {
      const value = Number(stakeAmount) * Number(conversions.fromERC20);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS,
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [stakeAmount, conversions]);

  const handleClick = useCallback(() => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET_CLICK, {
        cta_location: "center_wiget",
        cta: CONNECT_WALLET_CTA,
      });
    } else {
      if (!isRestakeSupported) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        // Calculate amountOut once for reuse
        const amountOut = `${
          Number(returns)
            ? formatNumber(Number(returns), false, MAX_FRACTION_DIGITS, true)
            : formatNumber(
                Number(convertedAmount),
                false,
                MAX_FRACTION_DIGITS,
                true,
              ) || 0
        }`;

        // Common transaction parameters
        const baseTransactionParams = {
          amount: stakeAmount,
          referralId,
          isStaderLedgerUser,
          interactionPool:
            config.interactionPools?.lrtDepositPool?.address || "",
          from: selectedERC20,
          to: config.xtoken.symbol,
          amountIn: stakeAmount,
          amountOut,
        };

        if (isApproved) {
          if (!stakeAmount) {
            setError(
              "This field can't be blank or zero. Please provide valid input.",
            );
            return;
          }

          trackEvent(STAKE_CTA, {
            amount: stakeAmount,
          });

          if (isStakingFull) {
            setIsStakeConfirmMoal(true);
            return;
          }

          emitReduxEvent(EVENTS.HANDLE_REVIEW_TRANSACTION, {
            ...baseTransactionParams,
            nextAction: EVENTS.HANDLE_STAKE,
          });

          setStakeAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_REVIEW_TRANSACTION, {
            ...baseTransactionParams,
            nextAction: EVENTS.HANDLE_ERC20_APPROVE,
          });

          trackEvent(APPROVE_STAKING_CLICK, { asset: selectedERC20 });
        }
      }
    }
  }, [
    isConnected,
    openConnectModal,
    isRestakeSupported,
    isApproved,
    stakeAmount,
    isStakingFull,
    referralId,
    isStaderLedgerUser,
    config.interactionPools?.lrtDepositPool?.address,
    config.xtoken.symbol,
    selectedERC20,
    returns,
    convertedAmount,
  ]);

  const handleMaxClick = useCallback(() => {
    if (selectedERC20 === ETH) {
      const amount = Math.max(
        0,
        tokenAmount - (Number(estimatedGasFee) || 0),
      ).toString();
      setStakeAmount(toFixedWithoutRounding(amount.toString(), 4));
    } else {
      setStakeAmount(toFixedWithoutRounding(tokenAmount.toString(), 4));
    }
  }, [tokenAmount]);

  return (
    <Box>
      <Box
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        color="textSecondary"
      >
        <TokenInput
          value={stakeAmount}
          onChange={(value: string) => handleChange(value)}
          error={error}
          onMaxClick={handleMaxClick}
          tokenBalance={
            tokenAmount !== undefined && tokenAmount !== null
              ? formatNumber(Number(tokenAmount), false, 4, true)
              : "-"
          }
          showBalance={isConnected}
          selectedMode={selectedMode}
          handleModeChange={handleModeChange}
        />
        <TransactionSummary
          items={[
            {
              label: "You will get",
              value: `${
                Number(returns)
                  ? formatNumber(
                      Number(returns),
                      false,
                      MAX_FRACTION_DIGITS,
                      true,
                    )
                  : formatNumber(
                      Number(convertedAmount),
                      false,
                      MAX_FRACTION_DIGITS,
                      true,
                    ) || 0
              } ${config.xtoken.symbol}`,
            },
            {
              label: "Exchange rate",
              value:
                conversions.toERC20 &&
                `1 ${config.xtoken.symbol} = ${toFixedWithoutRounding(
                  conversions.toERC20.toString(),
                  4,
                )} ${selectedERC20}`,
              condition: !!conversions.toERC20,
            },
            {
              label: "Maximum deposit",
              value:
                poolData?.poolLimit &&
                `${numberToCurrency(
                  poolData.poolLimit.toString(),
                )} ${selectedERC20}`,
              condition: !isEthereumChain && poolData.poolLimit > 0.00001,
            },
            {
              label: "Minting fees",
              value: depositFeeP && `${depositFeeP * 100}%`,
              condition: !!depositFeeP,
              tooltip: "Covers bridging, minting and restaking cost",
            },
            {
              label: <span>My {config.xtoken.symbol}</span>,
              value: (
                <div className="flex items-center">
                  <span>
                    {formatNumber(
                      Number(xTokenTokenBalance),
                      false,
                      Number(xTokenTokenBalance) < 0.0001 ? 6 : 4,
                      true,
                    ) || "-"}
                  </span>
                  {!isLedgerConnected && <AddXSymbol xSymbol={config.xtoken} />}
                </div>
              ),
              condition: isConnected,
            },
          ]}
        />
      </Box>

      <div className="flex flex-col gap-3">
        <RecoveryNotice className="mt-6" context="deposit" />
        <Button
          variant="primary"
          className="w-full py-3 mt-2 text-[12px] md:text-[14px] 2xl:text-[18px]"
          onClick={handleClick}
          disabled={
            !!error ||
            isDepositPoolPaused ||
            (isPoolSizeExceeded && isConnected) ||
            (!stakeAmount && isConnected)
          }
        >
          {getButtonText()}
        </Button>

        {isConnected && isYieldVaultSupportedChain && !isDepositPoolPaused ? (
          <StakeWithHGEth
            amount={stakeAmount}
            selectedToken={selectedERC20}
            error={error}
            isConnected={isConnected}
            isPoolSizeExceeded={isPoolSizeExceeded}
            variant="outline"
          />
        ) : null}
      </div>

      {isStakingFull && (
        <StakeConfirmation
          isOpen={isStakeConfirmModal}
          onClose={() => setIsStakeConfirmMoal(false)}
          onPrimaryClick={() => {
            setIsStakeConfirmMoal(false);
            emitReduxEvent(EVENTS.HANDLE_STAKE, {
              amount: stakeAmount,
              referralId,
            });
            setStakeAmount("");
          }}
        />
      )}
    </Box>
  );
};

export default StakeComponent;
